import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { deleteInventoryItem, inventoryItemSnapshotListener, inventoryHistorySnapshotListener } from '../../../repository/InventoryDAO';
import { IconButton, Card, Typography, CardContent, Grid, Button, useMediaQuery, Menu, MenuItem, ListItemIcon, Stack, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { Loading } from '../../../components/loading/Loading';
import { Edit, Photo, QrCode, Handyman, MoreVert, Delete } from '@mui/icons-material';
import AppSidebar from '../../../components/navbar/DesktopAppSidebar';
import { useOrganizationContext } from '../../../context/organization_context/OrganizationContext';
import { QRCodeSVG } from 'qrcode.react';
import ReactDOM from 'react-dom/client';
import EditInventoryLinkModal from './modals/EditInventoryLinkModal';
import { useToast, ToastType } from '../../../context/toast/ToastContext';
import ConfirmDeleteModal from '../../../components/modals/ConfirmDeleteModal';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../resources/strings/StringKeys';
import EditInventoryModal from './modals/EditInventoryModal';
import AdjustInventoryModal from './modals/AdjustInventoryModal';
import ImageUploadModal from '../../../components/image_upload_modal/ImageUploadModal';
import { ImageUploadType } from '../../../components/image_upload_modal/ImageUploadType';
import CustomPagination from '../../../components/pagination/CustomPagination'; 
import { useLoadingContext } from '../../../context/loading/LoadingContext';
import { IconPhoto } from '@tabler/icons-react';
import { useInventoryContext } from '../../../context/inventory_context/InventoryContext';
import usePermissions from '../../../utils/permissions/UsePermissions';
import { Modules } from '../../../utils/permissions/Modules';
import { PermissionTypes } from '../../../utils/permissions/PermissionTypes';

const InventoryItemDetail = () => {
  const { itemId } = useParams();
  const [item, setItem] = useState(null);
  const [history, setHistory] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [showEditLinkModal, setShowEditLinkModal] = useState(false);
  const [showAddImageModal, setShowAddImageModal] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showEditInventoryItemModal, setShowEditInventoryItemModal] = useState(false);
  const [showAdjustInventoryModal, setShowAdjustInventoryModal] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getSupplierByID, getLocationByID } = useInventoryContext();
  const { organization, getEmployeeByID } = useOrganizationContext();
  const qrRef = useRef();
  const { setLoadingData } = useLoadingContext();
  const { showToast } = useToast();
  const isMobile = useMediaQuery('(max-width:600px)');
  const [anchorEl, setAnchorEl] = useState(null);
  const { hasPermission } = usePermissions();

  useEffect(() => {
    if (organization && itemId) {
      const unsubscribeInventoryItem = inventoryItemSnapshotListener(organization.id, itemId, setItem);
      const unsubscribeInventoryHistory = inventoryHistorySnapshotListener(organization.id, itemId, (historyData) => {
        const sortedHistory = historyData.sort((a, b) => b.update_date.seconds - a.update_date.seconds);
        setHistory(sortedHistory);
      });

      return () => {
        unsubscribeInventoryItem();
        unsubscribeInventoryHistory();
      };
    }
  }, [itemId, organization]);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`<html><head><title>${t(StringKeys.PRINT_QR_CODE)}</title>`);
    printWindow.document.write('<style>');
    printWindow.document.write(`
        @media print { 
            body {
                margin: 0;
                padding: 0;
            }
            .print-only { 
                display: block;
            }
            .page-container {
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 8.5in;
                padding: 0.25in;
                box-sizing: border-box;
            }
            .qr-container { 
                display: inline-block;
                margin: 0.1in;
                padding: 0.1in;
                border: 1px solid #ccc;
                text-align: center;
                width: 2.5in;
                height: 3.8in;
                vertical-align: top;
                box-sizing: border-box;
            }
            .image-container {
                width: 0.8in;
                height: 0.8in;
                margin: 0.1in auto;
                display: flex;
                align-items: center;
                justify-content: center;
            }
            .image-container img {
                max-width: 100%;
                max-height: 100%;
                object-fit: contain;
            }
            @page {
                size: letter landscape;
                margin: 0;
            }
            h4 {
                margin: 0.1in 0;
                min-height: 0.3in;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 12pt;
            }
            p {
                margin: 0.05in 0;
                font-size: 10pt;
            }
            .qr-code-container {
                margin: 0.1in 0;
            }
            .qr-code-container svg {
                width: 1.2in;
                height: 1.2in;
            }
        }
    `);
    printWindow.document.write('</style></head><body><div class="page-container">');
    
    const itemUrl = window.location.href;
    printWindow.document.write(`
        <div class="qr-container">
            <h4>${item.name}</h4>
            <p>${t(StringKeys.MINIMUM_QUANTITY)}: ${item.minimum_quantity}</p>
            <p>${t(StringKeys.REORDER_QUANTITY)}: ${item.reorder_quantity}</p>
            <div class="image-container">
                ${item.image_url 
                    ? `<img src="${item.image_url}" alt="Item Image" />` 
                    : `<svg width="100%" height="100%" viewBox="0 0 100 100">
                        <rect width="100" height="100" fill="#f0f0f0"/>
                        <text x="50" y="50" font-family="Arial" font-size="12" fill="#666" text-anchor="middle" dominant-baseline="middle">No Image</text>
                       </svg>`
                }
            </div>
            <div class="qr-code-container" id="qr-code"></div>
        </div>
    `);
    
    printWindow.document.write('</div></body></html>');
    printWindow.document.close();

    const tempContainer = document.createElement('div');
    tempContainer.style.display = 'none';
    document.body.appendChild(tempContainer);

    const qrCodeContainer = document.createElement('div');
    qrCodeContainer.id = 'temp-qr';
    tempContainer.appendChild(qrCodeContainer);

    const root = ReactDOM.createRoot(qrCodeContainer);
    root.render(<QRCodeSVG value={itemUrl} size={128} />);

    setTimeout(() => {
        const svg = qrCodeContainer.querySelector('svg');
        if (svg) {
            const qrContainer = printWindow.document.getElementById('qr-code');
            qrContainer.appendChild(svg.cloneNode(true));
        }
        
        setTimeout(() => {
            printWindow.print();
            document.body.removeChild(tempContainer);
        }, 500);
    }, 0);
  };

  const handleDeleteItem = async () => {
      try {
          setLoadingData(true);
          var result = await deleteInventoryItem(organization.id, item.id);
          if(result.success) {
            showToast(t(StringKeys.SUCCESSFULLY_DELETED_X, {x:`${item.name}`}), ToastType.SUCCESS);
            navigate('/inventory'); 
          } else showToast(t(StringKeys.FAILED_TO_DELETE_X, {x:`${item.name}`}))
      } catch (error) {
        showToast(t(StringKeys.FAILED_TO_DELETE_X, {x:`${item.name}`}))
      } finally {
          setLoadingData(false);
      }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedHistory = history.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (!item) return <AppSidebar content={<Loading />} />;

  return (
    <AppSidebar
      content={
        <>
          <Stack gap={4}>
            {/* Header section */}
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                {item.image_url ? (
                  <img
                    src={item.image_url}
                    alt={"Item Image"}
                    style={{ width: isMobile ? '70px' : '80px', height: isMobile ? '70px' : '80px', cursor: hasPermission(Modules.INVENTORY, PermissionTypes.WRITE) ? 'pointer' : 'default' }}
                    onClick={() => hasPermission(Modules.INVENTORY, PermissionTypes.WRITE) && setShowAddImageModal(true)}
                  />
                ) : (
                  hasPermission(Modules.INVENTORY, PermissionTypes.WRITE) && (
                    <IconButton onClick={() => setShowAddImageModal(true)}>
                      <IconPhoto size={40} fontSize="large" />
                    </IconButton>
                  )
                )}
              </Grid>
              {/* Action buttons */}
              {!isMobile && (
                <Grid item xs>
                  <Stack direction="row" spacing={2}>
                    {hasPermission(Modules.INVENTORY, PermissionTypes.WRITE) && (
                      <>
                        <Button
                          variant="text"
                          startIcon={<Edit />}
                          onClick={() => setShowEditInventoryItemModal(true)}
                          sx={{ textTransform: 'none', fontSize: '1rem' }}
                        >
                          {t(StringKeys.EDIT)}
                        </Button>
                        <Button
                          variant="text"
                          startIcon={<Handyman />}
                          onClick={() => setShowAdjustInventoryModal(true)}
                          sx={{ textTransform: 'none', fontSize: '1rem' }}
                        >
                          {t(StringKeys.ADJUST)}
                        </Button>
                        <Button 
                          variant="text" 
                          startIcon={<QrCode />} 
                          onClick={() => {handlePrint()}}
                          sx={{ textTransform: 'none', fontSize: '1rem' }}
                        >
                          {t(StringKeys.PRINT_QR_CODE)}
                        </Button>
                        <Button 
                          variant="text" 
                          startIcon={<Delete />} 
                          onClick={() => setConfirmOpen(true)} 
                          sx={{ textTransform: 'none', fontSize: '1rem' }}
                        >
                          {t(StringKeys.DELETE)}
                        </Button>
                      </>
                    )}
                  </Stack>
                </Grid>
              )}
              {isMobile && hasPermission(Modules.INVENTORY, PermissionTypes.WRITE) && (
                <Grid item xs="auto">
                  <IconButton onClick={handleMenuClick}>
                    <MoreVert />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                  >
                    <MenuItem onClick={() => { setShowEditInventoryItemModal(true); handleMenuClose(); }}>
                      <ListItemIcon>
                        <Edit fontSize="small" />
                      </ListItemIcon>
                      {t(StringKeys.EDIT)}
                    </MenuItem>
                    <MenuItem onClick={() => { setShowAdjustInventoryModal(true); handleMenuClose(); }}>
                      <ListItemIcon>
                        <Handyman fontSize="small" />
                      </ListItemIcon>
                      {t(StringKeys.ADJUST)}
                    </MenuItem>
                    <MenuItem onClick={() => { handlePrint(); handleMenuClose(); }}>
                      <ListItemIcon>
                        <QrCode fontSize="small" />
                      </ListItemIcon>
                      {t(StringKeys.PRINT_QR_CODE)}
                    </MenuItem>
                    <MenuItem onClick={() => { setConfirmOpen(true); handleMenuClose(); }}>
                      <ListItemIcon>
                        <Delete fontSize="small" />
                      </ListItemIcon>
                      {t(StringKeys.DELETE)}
                    </MenuItem>
                  </Menu>
                </Grid>
              )}
            </Grid>

            {/* Item Information Card */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant={isMobile ? "h6" : "h5"}>{item.name}</Typography>
                <Typography>{t(StringKeys.PRODUCT_SKU)}: {item.sku}</Typography>
                <Typography>{t(StringKeys.QUANTITY)}: {item.quantity}</Typography>
                <Typography>{t(StringKeys.MINIMUM_QUANTITY)}: {item.minimum_quantity}</Typography>
                <Typography>{t(StringKeys.PRICE)}: ${item.price}</Typography>
                <Typography>{t(StringKeys.REORDER_QUANTITY)}: {item.reorder_quantity}</Typography>
                <Typography>{t(StringKeys.UNIT_OF_MEASURE)}: {item.unit_of_measure}</Typography>
                <Typography>{t(StringKeys.SUPPLIER)}: {getSupplierByID(item.supplier)?.name || t(StringKeys.UNASSIGNED)}</Typography>
                <Typography>{t(StringKeys.LOCATION)}: {getLocationByID(item.location)?.name || t(StringKeys.UNASSIGNED)}</Typography>
                <Typography variant="body1">
                  {t(StringKeys.PRODUCT_LINK)}: {item.link ? (
                    <Link href={item.link} target="_blank" rel="noopener noreferrer">
                      {item.link}
                    </Link>
                  ) : "N/A"}
                </Typography>
              </CardContent>
            </Card>

            {/* Item History Table */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant={isMobile ? "h6" : "h5"}>{t(StringKeys.ITEM_HISTORY)}</Typography>
                {history.length === 0 ? (
                  <TableContainer>
                      <Table aria-label="no-notes-table">
                          <TableBody>
                              <TableRow>
                                  <TableCell colSpan={3} align="center">
                                      <Typography variant="body2">{t(StringKeys.NO_HISTORY)}</Typography>
                                  </TableCell>
                              </TableRow>
                          </TableBody>
                      </Table>
                  </TableContainer>
                ) : (
                  <>
                    <TableContainer
                    >
                    <Table sx={{minWidth:750}}>

                    <TableHead>
                          <TableRow>
                            <TableCell>{t(StringKeys.DATE)}</TableCell>
                            <TableCell>{t(StringKeys.ORDER)}</TableCell>
                            <TableCell>{t(StringKeys.QUANTITY_CHANGE)}</TableCell>
                            <TableCell>{t(StringKeys.REASON)}</TableCell>
                            <TableCell>{t(StringKeys.UPDATED_BY)}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                        {paginatedHistory.map((historyEntry, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <strong>
                                {`${new Date(historyEntry.update_date.seconds * 1000).toLocaleDateString()} - ${new Date(historyEntry.update_date.seconds * 1000).toLocaleTimeString()}`}
                              </strong>
                            </TableCell>
                            <TableCell>
                              {historyEntry.order_id ? (
                                hasPermission(Modules.INVENTORY_ORDERS, PermissionTypes.READ) ? (
                                  <Link
                                    onClick={() => {
                                      navigate(`/inventory-orders/${historyEntry.order_id}`);
                                    }}
                                    sx={{ cursor: "pointer" }}
                                  >
                                    {historyEntry.order_id}
                                  </Link>
                                ) : (
                                  historyEntry.order_id
                                )
                              ) : (
                                t(StringKeys.NONE)
                              )}
                            </TableCell>
                            <TableCell>
                              {historyEntry.new_value < historyEntry.old_value ? (
                                t(StringKeys.REDUCED_TO_X_FROM_Y, {x:`${historyEntry.new_value}`, y:`${historyEntry.old_value}`})
                              ) : (
                                t(StringKeys.INCREASED_TO_X_FROM_Y, {x: `${historyEntry.new_value}`, y:`${historyEntry.old_value}`})
                              )}
                            </TableCell>
                            <TableCell>
                              {t(historyEntry.reason)}
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const employee = getEmployeeByID(historyEntry.updated_by);
                                if (!employee) return t(StringKeys.UNKNOWN);
                                
                                return hasPermission(Modules.EMPLOYEES, PermissionTypes.READ) ? (
                                  <Link
                                    onClick={() => navigate(`/employees/${historyEntry.updated_by}`)}
                                    sx={{ cursor: 'pointer' }}
                                  >
                                    {`${employee.first_name} ${employee.last_name}`}
                                  </Link>
                                ) : (
                                  `${employee.first_name} ${employee.last_name}`
                                );
                              })()}
                            </TableCell>
                          </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <CustomPagination
                      rowsPerPageOptions={[ 10, 25, 100, { label: 'All', value: -1 }]}
                      component="div"
                      count={history.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </>
                )}
              </CardContent>
            </Card>

            <div ref={qrRef} style={{ display: 'none' }}>
              <QRCodeSVG value={window.location.href} size={128} />
            </div>

            <EditInventoryLinkModal
              show={showEditLinkModal}
              onHide={() => setShowEditLinkModal(false)}
              item={item}
            />
            <ImageUploadModal
              show={showAddImageModal}
              onHide={() => setShowAddImageModal(false)}
              modalTitle={t(StringKeys.ADD_NEW_IMAGE)}
              cropShape="rect"
              organization={organization.id}
              itemId={item.id}
              uploadType={ImageUploadType.InventoryImage}
            />
            <EditInventoryModal
              show={showEditInventoryItemModal}
              onHide={() => { setShowEditInventoryItemModal(false) }}
              inventoryItem={item}
            />
            <AdjustInventoryModal
              show={showAdjustInventoryModal}
              onHide={() => { setShowAdjustInventoryModal(false) }}
              item={item}
            />
            <ConfirmDeleteModal
              open={confirmOpen}
              onClose={() => setConfirmOpen(false)}
              onConfirm={() => { handleDeleteItem(); setConfirmOpen(false); }}
              item={item.name}
            />
          </Stack>
        </>
      }
    />
  );
};

export default InventoryItemDetail;
